import { apiRequest } from './axiosConfig';
import type { BannerAbTest, BannerAbTestEstimate, Paginated } from '../../types/bannerAbTest';

/**
 * オートバナーABテスト API クライアント（/api/banner-ab-tests/*）。
 * レスポンスは { success, data } で、ここでは data だけ返す。
 */
type Envelope<T> = { success: boolean; data: T };

export async function estimateBannerAbTest(imageIds: number[], personaCount: number): Promise<BannerAbTestEstimate> {
  const params = new URLSearchParams();
  imageIds.forEach((id) => params.append('image_ids[]', String(id)));
  params.set('persona_count', String(personaCount));
  const res = await apiRequest.get<Envelope<BannerAbTestEstimate>>(`/banner-ab-tests/estimate?${params.toString()}`);
  return res.data;
}

export async function createBannerAbTest(
  imageIds: number[],
  personaCount: number,
): Promise<{ id: number; credits_estimated: number; status: string }> {
  const res = await apiRequest.post<Envelope<{ id: number; credits_estimated: number; status: string }>>('/banner-ab-tests', {
    mode: 'pairwise',
    image_ids: imageIds,
    persona_count: personaCount,
  });
  return res.data;
}

/**
 * 自動改善ループの見積り。予算は未設定なら送らない（サーバ側で「上限なし」になる）。
 * 1ラウンドの派生枚数は返り値の challengers を使う（フロントに定数を置かない）。
 */
export async function estimateBannerAbTestLoop(
  championImageId: number,
  personaCount: number,
  maxRounds: number,
  budgetCredits?: number | null,
): Promise<BannerAbTestEstimate> {
  const params = new URLSearchParams();
  params.set('mode', 'auto_loop');
  params.set('champion_image_id', String(championImageId));
  params.set('persona_count', String(personaCount));
  params.set('max_rounds', String(maxRounds));
  if (budgetCredits != null) params.set('budget_credits', String(budgetCredits));
  const res = await apiRequest.get<Envelope<BannerAbTestEstimate>>(`/banner-ab-tests/estimate?${params.toString()}`);
  return res.data;
}

export async function createBannerAbTestLoop(
  championImageId: number,
  personaCount: number,
  maxRounds: number,
  budgetCredits?: number | null,
): Promise<{ id: number; credits_estimated: number; status: string }> {
  const body: Record<string, unknown> = {
    mode: 'auto_loop',
    champion_image_id: championImageId,
    persona_count: personaCount,
    max_rounds: maxRounds,
  };
  if (budgetCredits != null) body.budget_credits = budgetCredits;
  const res = await apiRequest.post<Envelope<{ id: number; credits_estimated: number; status: string }>>('/banner-ab-tests', body);
  return res.data;
}

export async function listBannerAbTests(page = 1): Promise<Paginated<BannerAbTest>> {
  const res = await apiRequest.get<Envelope<Paginated<BannerAbTest>>>(`/banner-ab-tests?page=${page}`);
  return res.data;
}

export async function getBannerAbTest(id: number, includeVotes = false): Promise<BannerAbTest> {
  const suffix = includeVotes ? '?include_votes=1' : '';
  const res = await apiRequest.get<Envelope<BannerAbTest>>(`/banner-ab-tests/${id}${suffix}`);
  return res.data;
}

export async function cancelBannerAbTest(id: number): Promise<void> {
  await apiRequest.post<Envelope<unknown>>(`/banner-ab-tests/${id}/cancel`);
}
